'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface FollowedCaretaker {
  id: string;
  name: string;
  role: string;
  city?: string;
  stage?: string;
  questsCompleted: number;
  garmentShared: boolean;
  isFollowing: boolean;
  isMutual: boolean;
  lastActive?: string;
}

interface ActivityItem {
  id: string;
  actorName: string;
  actorRole: string;
  activityType: string;
  activityLabel: string;
  activityDetail?: string;
  createdAt: string;
}

interface Cohort {
  id: string;
  name: string;
  role: string;
  description: string;
  icon: string;
  color: string;
  memberCount: number;
}

interface CommunityTask {
  id: string;
  title: string;
  description?: string;
  category: string;
  status: string;
  priority: string;
  creatorName?: string;
  creatorRole?: string;
  volunteerCount: number;
  createdAt: string;
}

interface PrayerEntry {
  name: string;
  arabic: string;
  time: string;
  completed: boolean;
}

interface JusticeCase {
  id: string;
  caseTitle: string;
  caseType: string;
  status: string;
  description?: string;
  createdAt: string;
}

interface LearningItem {
  id: string;
  moduleLabel: string;
  moduleHref?: string;
  scheduledDate: string;
  completed: boolean;
  notes?: string;
}

interface ResourceFavorite {
  id: string;
  resourceType: string;
  resourceLabel: string;
  resourceHref?: string;
  notes?: string;
}

interface CohortMessage {
  id: string;
  authorName?: string;
  authorRole?: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
}

interface InsightGoal {
  id: string;
  goalType: string;
  goalLabel: string;
  targetValue: number;
  currentValue: number;
  deadline?: string;
  isCompleted: boolean;
}

interface InsightMetrics {
  questsCompleted: number;
  communityContributions: number;
  garmentsShared: number;
  followersGained: number;
  badgesEarned: number;
  alhamdulilahCount: number;
  modulesVisited: number;
  reactionsReceived: number;
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, string> = {
  'inner-seeker': '#8a8ab8',
  'community-caretaker': '#2d7a47',
  'academic': '#d4a82a',
  'activist': '#2d8a87',
};

const ROLE_ICONS: Record<string, string> = {
  'inner-seeker': '◎',
  'community-caretaker': '⬡',
  'academic': '✦',
  'activist': '⌘',
};

const ROLE_LABELS: Record<string, string> = {
  'inner-seeker': 'Inner Seeker',
  'community-caretaker': 'Community Caretaker',
  'academic': 'Academic',
  'activist': 'Activist',
};

const ACTIVITY_ICONS: Record<string, string> = {
  quest_completed: '⌘',
  badge_earned: '✦',
  garment_shared: '⬡',
  milestone: '◎',
  cohort_joined: '◈',
  resource_saved: '☽',
};

const ACTIVITY_COLORS: Record<string, string> = {
  quest_completed: '#D4A82A',
  badge_earned: '#8a8ab8',
  garment_shared: '#2d8a87',
  milestone: '#2d7a47',
  cohort_joined: '#c9a84c',
  resource_saved: '#2d7a47',
};

const PRAYERS: PrayerEntry[] = [
  { name: 'Fajr', arabic: 'الفجر', time: 'Dawn', completed: false },
  { name: 'Dhuhr', arabic: 'الظهر', time: 'Midday', completed: false },
  { name: 'Asr', arabic: 'العصر', time: 'Afternoon', completed: false },
  { name: 'Maghrib', arabic: 'المغرب', time: 'Sunset', completed: false },
  { name: 'Isha', arabic: 'العشاء', time: 'Night', completed: false },
];

const DEMO_CARETAKERS: FollowedCaretaker[] = [
  { id: 'c1', name: 'Sister Amira', role: 'community-caretaker', city: 'Atlanta', stage: 'Recognition', questsCompleted: 7, garmentShared: true, isFollowing: true, isMutual: true, lastActive: '2h ago' },
  { id: 'c2', name: 'Brother Tariq', role: 'inner-seeker', city: 'Portland', stage: 'Examination', questsCompleted: 4, garmentShared: false, isFollowing: true, isMutual: false, lastActive: '1d ago' },
  { id: 'c3', name: 'Dr. Yusuf', role: 'academic', city: 'Durham', stage: 'Re-Assembly', questsCompleted: 12, garmentShared: true, isFollowing: true, isMutual: true, lastActive: '3h ago' },
  { id: 'c4', name: 'Ustadha Khadija', role: 'activist', city: 'Houston', stage: 'Uboor', questsCompleted: 9, garmentShared: true, isFollowing: true, isMutual: false, lastActive: '5h ago' },
  { id: 'c5', name: 'Imam Hassan', role: 'community-caretaker', city: 'Chicago', stage: 'Encounter', questsCompleted: 2, garmentShared: false, isFollowing: true, isMutual: true, lastActive: '2d ago' },
  { id: 'c6', name: 'Nadia Al-Rashid', role: 'inner-seeker', city: 'Detroit', stage: 'Examination', questsCompleted: 5, garmentShared: true, isFollowing: false, isMutual: false, lastActive: '6h ago' },
];

const DEMO_ACTIVITY: ActivityItem[] = [
  { id: 'a1', actorName: 'Sister Amira', actorRole: 'community-caretaker', activityType: 'quest_completed', activityLabel: 'Completed: Justice Mapping Quest', activityDetail: 'Mapped 3 community resources using the MRG framework', createdAt: '2h ago' },
  { id: 'a2', actorName: 'Dr. Yusuf', actorRole: 'academic', activityType: 'garment_shared', activityLabel: 'Shared garment visualization', activityDetail: 'Names-first, Keys-deep scholarship pathway', createdAt: '4h ago' },
  { id: 'a3', actorName: 'Brother Tariq', actorRole: 'inner-seeker', activityType: 'badge_earned', activityLabel: 'Earned: Framework Walker', activityDetail: 'Visited 4 or more modules in the framework', createdAt: '1d ago' },
  { id: 'a4', actorName: 'Ustadha Khadija', actorRole: 'activist', activityType: 'milestone', activityLabel: 'Reached Recognition stage', activityDetail: 'Crossed into the Recognition stage of the Alhamdulilah journey', createdAt: '1d ago' },
  { id: 'a5', actorName: 'Imam Hassan', actorRole: 'community-caretaker', activityType: 'cohort_joined', activityLabel: 'Joined Community Caretakers Network', activityDetail: 'Now part of the cohort with 83 members', createdAt: '2d ago' },
  { id: 'a6', actorName: 'Sister Amira', actorRole: 'community-caretaker', activityType: 'resource_saved', activityLabel: 'Saved: Atlanta Housing Resources', activityDetail: 'Added to resource favorites from MRG', createdAt: '3d ago' },
];

const DEMO_TASKS: CommunityTask[] = [
  { id: 't1', title: 'Map halal food resources in South Atlanta', description: 'Using the MRG city mapping tool to document accessible halal food sources', category: 'resources', status: 'open', priority: 'high', creatorName: 'Sister Amira', creatorRole: 'community-caretaker', volunteerCount: 3, createdAt: '2d ago' },
  { id: 't2', title: 'Organize know-your-rights workshop', description: 'Partner with local masjid to host a justice-focused workshop for returning citizens', category: 'justice', status: 'in_progress', priority: 'high', creatorName: 'Ustadha Khadija', creatorRole: 'activist', volunteerCount: 7, createdAt: '3d ago' },
  { id: 't3', title: 'IRL Lens study circle', description: 'Weekly gathering to practice the IRL Lens together and share observations', category: 'education', status: 'open', priority: 'medium', creatorName: 'Brother Tariq', creatorRole: 'inner-seeker', volunteerCount: 12, createdAt: '5d ago' },
  { id: 't4', title: 'Compile Names of Allah study guide', description: 'Academic resource connecting the 99 Names to real-world caretaking contexts', category: 'education', status: 'open', priority: 'medium', creatorName: 'Dr. Yusuf', creatorRole: 'academic', volunteerCount: 5, createdAt: '1w ago' },
];

const DEMO_JUSTICE_CASES: JusticeCase[] = [
  { id: 'j1', caseTitle: 'Housing assistance for returning citizen', caseType: 'housing', status: 'resolved', description: 'Connected brother with transitional housing through MRG resources', createdAt: '1w ago' },
  { id: 'j2', caseTitle: 'Employment discrimination case', caseType: 'employment', status: 'active', description: 'Documenting and referring to legal aid organization', createdAt: '3d ago' },
];

const DEMO_LEARNING: LearningItem[] = [
  { id: 'l1', moduleLabel: 'IRL Lens Walkthrough', moduleHref: '/irl-lens', scheduledDate: 'Today', completed: false },
  { id: 'l2', moduleLabel: 'Keys to Kulli Haal — Key 3', moduleHref: '/keys-to-kulli-haal', scheduledDate: 'Tomorrow', completed: false },
  { id: 'l3', moduleLabel: 'Names Reference — Al-Adl', moduleHref: '/names-reference', scheduledDate: 'Fri', completed: true },
];

const DEMO_RESOURCES: ResourceFavorite[] = [
  { id: 'r1', resourceType: 'mrg_item', resourceLabel: 'Atlanta Housing Resources', resourceHref: '/muslim-resource-guide', notes: 'For justice-involved brothers' },
  { id: 'r2', resourceType: 'name', resourceLabel: 'Al-Adl — The Just', resourceHref: '/names-reference', notes: 'Core to organizing work' },
  { id: 'r3', resourceType: 'key', resourceLabel: 'Key 4: Mizan', resourceHref: '/keys-to-kulli-haal', notes: 'Balance in all things' },
];

const DEMO_COHORT_MESSAGES: CohortMessage[] = [
  { id: 'm1', authorName: 'Sister Amira', authorRole: 'community-caretaker', content: 'Alhamdulilah — just finished mapping the food desert in our neighborhood. The MRG tool made it so clear. Who else is working on resource mapping?', isPinned: true, createdAt: '3h ago' },
  { id: 'm2', authorName: 'Imam Hassan', authorRole: 'community-caretaker', content: 'MashaAllah Sister Amira. We did something similar in Chicago. Happy to share our template.', isPinned: false, createdAt: '2h ago' },
  { id: 'm3', authorName: 'Anonymous Caretaker', authorRole: 'community-caretaker', content: 'The justice case log feature has been so helpful for tracking the brothers we are supporting post-release.', isPinned: false, createdAt: '1h ago' },
];

const DEMO_GOALS: InsightGoal[] = [
  { id: 'g1', goalType: 'quests', goalLabel: 'Complete 10 quests', targetValue: 10, currentValue: 7, isCompleted: false },
  { id: 'g2', goalType: 'garments_shared', goalLabel: 'Share 3 garments', targetValue: 3, currentValue: 1, isCompleted: false },
  { id: 'g3', goalType: 'followers', goalLabel: 'Grow to 20 followers', targetValue: 20, currentValue: 14, isCompleted: false },
  { id: 'g4', goalType: 'contributions', goalLabel: 'Make 5 community contributions', targetValue: 5, currentValue: 5, isCompleted: true },
];

const DEMO_METRICS: InsightMetrics = {
  questsCompleted: 7,
  communityContributions: 5,
  garmentsShared: 1,
  followersGained: 14,
  badgesEarned: 6,
  alhamdulilahCount: 142,
  modulesVisited: 8,
  reactionsReceived: 23,
};

// ─── UTILITY ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  if (!dateStr || dateStr.includes('ago') || dateStr === 'Today' || dateStr === 'Tomorrow') return dateStr;
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── PRAYER TRACKER WIDGET ────────────────────────────────────────────────────

function PrayerTrackerWidget() {
  const [prayers, setPrayers] = useState<PrayerEntry[]>(PRAYERS);
  const [reflection, setReflection] = useState('');

  const toggle = (idx: number) => {
    setPrayers(prev => prev.map((p, i) => i === idx ? { ...p, completed: !p.completed } : p));
  };

  const completed = prayers.filter(p => p.completed).length;

  return (
    <div className="rounded-xl border p-5" style={{ background: 'rgba(45,122,71,0.06)', borderColor: 'rgba(45,122,71,0.2)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>Prayer Tracker</h3>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(212,197,169,0.5)' }}>مواقيت الصلاة</p>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold" style={{ color: '#2d7a47' }}>{completed}/5</span>
          <p className="text-xs" style={{ color: 'rgba(212,197,169,0.5)' }}>today</p>
        </div>
      </div>
      <div className="space-y-2 mb-4">
        {prayers.map((prayer, idx) => (
          <button
            key={prayer.name}
            onClick={() => toggle(idx)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all"
            style={{
              background: prayer.completed ? 'rgba(45,122,71,0.15)' : 'rgba(212,197,169,0.04)',
              border: `1px solid ${prayer.completed ? 'rgba(45,122,71,0.4)' : 'rgba(212,197,169,0.1)'}`,
            }}
          >
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: prayer.completed ? '#2d7a47' : 'transparent', border: `1.5px solid ${prayer.completed ? '#2d7a47' : 'rgba(212,197,169,0.3)'}` }}>
                {prayer.completed && <span className="text-white text-xs">✓</span>}
              </div>
              <span className="text-sm font-medium" style={{ color: prayer.completed ? '#2d7a47' : 'rgba(212,197,169,0.8)' }}>{prayer.name}</span>
            </div>
            <div className="text-right">
              <span className="text-xs" style={{ color: 'rgba(212,197,169,0.4)' }}>{prayer.arabic}</span>
            </div>
          </button>
        ))}
      </div>
      <div className="w-full rounded-full h-1.5 mb-3" style={{ background: 'rgba(212,197,169,0.1)' }}>
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${(completed / 5) * 100}%`, background: '#2d7a47' }} />
      </div>
      <input
        value={reflection}
        onChange={e => setReflection(e.target.value)}
        placeholder="Add a reflection for today..."
        className="w-full text-xs px-3 py-2 rounded-lg outline-none"
        style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.1)', color: 'rgba(212,197,169,0.7)' }}
      />
    </div>
  );
}

// ─── COMMUNITY TASK BOARD WIDGET ──────────────────────────────────────────────

function CommunityTaskBoardWidget({ tasks }: { tasks: CommunityTask[] }) {
  const [filter, setFilter] = useState<'all' | 'open' | 'in_progress'>('all');
  const [newTask, setNewTask] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const filtered = tasks.filter(t => filter === 'all' || t.status === filter);

  const PRIORITY_COLORS: Record<string, string> = { high: '#D4A82A', medium: '#2d8a87', low: '#2d7a47' };
  const STATUS_COLORS: Record<string, string> = { open: '#2d7a47', in_progress: '#D4A82A', completed: '#8a8ab8' };

  return (
    <div className="rounded-xl border p-5" style={{ background: 'rgba(45,122,71,0.06)', borderColor: 'rgba(45,122,71,0.2)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>Community Task Board</h3>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(212,197,169,0.5)' }}>لوحة المهام المجتمعية</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="text-xs px-2 py-1 rounded-lg transition-all" style={{ background: 'rgba(45,122,71,0.15)', color: '#2d7a47', border: '1px solid rgba(45,122,71,0.3)' }}>
          + Add
        </button>
      </div>
      <div className="flex gap-1 mb-3">
        {(['all', 'open', 'in_progress'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} className="text-xs px-2 py-1 rounded-lg transition-all capitalize" style={{ background: filter === f ? 'rgba(45,122,71,0.2)' : 'transparent', color: filter === f ? '#2d7a47' : 'rgba(212,197,169,0.5)', border: `1px solid ${filter === f ? 'rgba(45,122,71,0.4)' : 'transparent'}` }}>
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>
      {showAdd && (
        <div className="mb-3 flex gap-2">
          <input value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="New task title..." className="flex-1 text-xs px-3 py-2 rounded-lg outline-none" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.15)', color: 'rgba(212,197,169,0.8)' }} />
          <button onClick={() => { setNewTask(''); setShowAdd(false); }} className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(45,122,71,0.2)', color: '#2d7a47' }}>Post</button>
        </div>
      )}
      <div className="space-y-2 max-h-56 overflow-y-auto">
        {filtered.map(task => (
          <div key={task.id} className="p-3 rounded-lg" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className="text-xs font-medium leading-tight" style={{ color: 'rgba(212,197,169,0.85)' }}>{task.title}</span>
              <span className="text-xs px-1.5 py-0.5 rounded shrink-0" style={{ background: `${STATUS_COLORS[task.status]}20`, color: STATUS_COLORS[task.status] }}>{task.status.replace('_', ' ')}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-xs" style={{ color: 'rgba(212,197,169,0.4)' }}>{ROLE_ICONS[task.creatorRole || ''] || '◎'} {task.creatorName}</span>
              <span className="text-xs ml-auto" style={{ color: PRIORITY_COLORS[task.priority] }}>● {task.priority}</span>
              <span className="text-xs" style={{ color: 'rgba(212,197,169,0.4)' }}>👥 {task.volunteerCount}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── JUSTICE CASE LOG WIDGET ──────────────────────────────────────────────────

function JusticeCaseLogWidget({ cases }: { cases: JusticeCase[] }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newCase, setNewCase] = useState('');

  const STATUS_COLORS: Record<string, string> = { active: '#D4A82A', resolved: '#2d7a47', referred: '#2d8a87', closed: '#8a8ab8' };
  const TYPE_ICONS: Record<string, string> = { housing: '🏠', legal: '⚖️', employment: '💼', education: '📚', general: '◎' };

  return (
    <div className="rounded-xl border p-5" style={{ background: 'rgba(45,138,135,0.06)', borderColor: 'rgba(45,138,135,0.2)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>Justice Case Log</h3>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(212,197,169,0.5)' }}>سجل قضايا العدالة</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(45,138,135,0.15)', color: '#2d8a87', border: '1px solid rgba(45,138,135,0.3)' }}>
          + Log
        </button>
      </div>
      {showAdd && (
        <div className="mb-3 flex gap-2">
          <input value={newCase} onChange={e => setNewCase(e.target.value)} placeholder="Case title..." className="flex-1 text-xs px-3 py-2 rounded-lg outline-none" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.15)', color: 'rgba(212,197,169,0.8)' }} />
          <button onClick={() => { setNewCase(''); setShowAdd(false); }} className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(45,138,135,0.2)', color: '#2d8a87' }}>Add</button>
        </div>
      )}
      <div className="space-y-2">
        {cases.map(c => (
          <div key={c.id} className="p-3 rounded-lg" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-1.5">
                <span>{TYPE_ICONS[c.caseType] || '◎'}</span>
                <span className="text-xs font-medium" style={{ color: 'rgba(212,197,169,0.85)' }}>{c.caseTitle}</span>
              </div>
              <span className="text-xs px-1.5 py-0.5 rounded shrink-0" style={{ background: `${STATUS_COLORS[c.status]}20`, color: STATUS_COLORS[c.status] }}>{c.status}</span>
            </div>
            {c.description && <p className="text-xs mt-1" style={{ color: 'rgba(212,197,169,0.5)' }}>{c.description}</p>}
            <p className="text-xs mt-1" style={{ color: 'rgba(212,197,169,0.35)' }}>{c.createdAt}</p>
          </div>
        ))}
        {cases.length === 0 && (
          <p className="text-xs text-center py-4" style={{ color: 'rgba(212,197,169,0.4)' }}>No cases logged yet. Begin documenting your justice work.</p>
        )}
      </div>
      <Link href="/muslim-resource-guide" className="block mt-3 text-xs text-center py-2 rounded-lg transition-all" style={{ background: 'rgba(45,138,135,0.1)', color: '#2d8a87', border: '1px solid rgba(45,138,135,0.2)' }}>
        Open MRG Resources →
      </Link>
    </div>
  );
}

// ─── LEARNING SCHEDULE WIDGET ─────────────────────────────────────────────────

function LearningScheduleWidget({ items }: { items: LearningItem[] }) {
  const [schedule, setSchedule] = useState(items);

  const toggle = (id: string) => {
    setSchedule(prev => prev.map(i => i.id === id ? { ...i, completed: !i.completed } : i));
  };

  const MODULE_LINKS = [
    { label: 'Guided Walkthrough', href: '/guided-walkthrough' },
    { label: 'IRL Lens', href: '/inner-reality-lens' },
    { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal' },
    { label: 'Names Reference', href: '/names-reference' },
    { label: 'Ayat in Creation', href: '/ayat-in-creation' },
    { label: 'Muslim Resource Guide', href: '/muslim-resource-guide' },
  ];

  return (
    <div className="rounded-xl border p-5" style={{ background: 'rgba(212,168,42,0.06)', borderColor: 'rgba(212,168,42,0.2)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>Learning Schedule</h3>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(212,197,169,0.5)' }}>جدول التعلم</p>
        </div>
        <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(212,168,42,0.15)', color: '#D4A82A' }}>
          {schedule.filter(i => i.completed).length}/{schedule.length} done
        </span>
      </div>
      <div className="space-y-2 mb-4">
        {schedule.map(item => (
          <div key={item.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
            <button onClick={() => toggle(item.id)} className="w-4 h-4 rounded-full shrink-0 flex items-center justify-center" style={{ background: item.completed ? '#D4A82A' : 'transparent', border: `1.5px solid ${item.completed ? '#D4A82A' : 'rgba(212,197,169,0.3)'}` }}>
              {item.completed && <span className="text-white text-xs">✓</span>}
            </button>
            <div className="flex-1 min-w-0">
              {item.moduleHref ? (
                <Link href={item.moduleHref} className="text-xs font-medium truncate block hover:underline" style={{ color: item.completed ? 'rgba(212,197,169,0.4)' : 'rgba(212,197,169,0.85)', textDecoration: item.completed ? 'line-through' : 'none' }}>
                  {item.moduleLabel}
                </Link>
              ) : (
                <span className="text-xs font-medium" style={{ color: item.completed ? 'rgba(212,197,169,0.4)' : 'rgba(212,197,169,0.85)' }}>{item.moduleLabel}</span>
              )}
            </div>
            <span className="text-xs shrink-0" style={{ color: 'rgba(212,197,169,0.35)' }}>{item.scheduledDate}</span>
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs mb-2" style={{ color: 'rgba(212,197,169,0.5)' }}>Quick add module:</p>
        <div className="flex flex-wrap gap-1">
          {MODULE_LINKS.map(m => (
            <Link key={m.href} href={m.href} className="text-xs px-2 py-1 rounded-lg transition-all" style={{ background: 'rgba(212,168,42,0.08)', color: 'rgba(212,197,169,0.6)', border: '1px solid rgba(212,168,42,0.15)' }}>
              {m.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── RESOURCE FAVORITES WIDGET ────────────────────────────────────────────────

function ResourceFavoritesWidget({ resources }: { resources: ResourceFavorite[] }) {
  const TYPE_ICONS: Record<string, string> = { mrg_item: '⬡', ayat: '☽', name: '✦', key: '⌘', phase: '◈', external: '◎' };
  const TYPE_COLORS: Record<string, string> = { mrg_item: '#2d7a47', ayat: '#2d7a47', name: '#8a8ab8', key: '#D4A82A', phase: '#5a5a8a', external: '#2d8a87' };

  return (
    <div className="rounded-xl border p-5" style={{ background: 'rgba(138,138,184,0.06)', borderColor: 'rgba(138,138,184,0.2)' }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>Resource Favorites</h3>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(212,197,169,0.5)' }}>المفضلة</p>
        </div>
        <span className="text-xs" style={{ color: 'rgba(212,197,169,0.4)' }}>{resources.length} saved</span>
      </div>
      <div className="space-y-2">
        {resources.map(r => (
          <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
            <span className="text-base shrink-0 mt-0.5" style={{ color: TYPE_COLORS[r.resourceType] || '#8a8ab8' }}>{TYPE_ICONS[r.resourceType] || '◎'}</span>
            <div className="flex-1 min-w-0">
              {r.resourceHref ? (
                <Link href={r.resourceHref} className="text-xs font-medium hover:underline" style={{ color: 'rgba(212,197,169,0.85)' }}>{r.resourceLabel}</Link>
              ) : (
                <span className="text-xs font-medium" style={{ color: 'rgba(212,197,169,0.85)' }}>{r.resourceLabel}</span>
              )}
              {r.notes && <p className="text-xs mt-0.5" style={{ color: 'rgba(212,197,169,0.45)' }}>{r.notes}</p>}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Link href="/muslim-resource-guide" className="text-xs text-center py-2 rounded-lg" style={{ background: 'rgba(45,122,71,0.1)', color: '#2d7a47', border: '1px solid rgba(45,122,71,0.2)' }}>Browse MRG</Link>
        <Link href="/names-reference" className="text-xs text-center py-2 rounded-lg" style={{ background: 'rgba(138,138,184,0.1)', color: '#8a8ab8', border: '1px solid rgba(138,138,184,0.2)' }}>Names Ref</Link>
      </div>
    </div>
  );
}

// ─── COHORT MESSAGE BOARD WIDGET ──────────────────────────────────────────────

function CohortMessageBoardWidget({ messages, cohortName, cohortColor }: { messages: CohortMessage[]; cohortName: string; cohortColor: string }) {
  const [newMsg, setNewMsg] = useState('');

  return (
    <div className="rounded-xl border p-5" style={{ background: `${cohortColor}0d`, borderColor: `${cohortColor}33` }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>Cohort Board</h3>
          <p className="text-xs mt-0.5 truncate max-w-[160px]" style={{ color: cohortColor }}>{cohortName}</p>
        </div>
        <span className="text-xs px-2 py-1 rounded-lg" style={{ background: `${cohortColor}20`, color: cohortColor, border: `1px solid ${cohortColor}40` }}>
          {messages.length} posts
        </span>
      </div>
      <div className="space-y-3 mb-4 max-h-52 overflow-y-auto">
        {messages.map(msg => (
          <div key={msg.id} className="p-3 rounded-lg" style={{ background: msg.isPinned ? `${cohortColor}0d` : 'rgba(212,197,169,0.03)', border: `1px solid ${msg.isPinned ? `${cohortColor}30` : 'rgba(212,197,169,0.08)'}` }}>
            {msg.isPinned && <span className="text-xs mb-1 block" style={{ color: cohortColor }}>📌 Pinned</span>}
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(212,197,169,0.8)' }}>{msg.content}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>{msg.authorName || 'Anonymous'}</span>
              <span className="text-xs ml-auto" style={{ color: 'rgba(212,197,169,0.35)' }}>{msg.createdAt}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Share with your cohort..." className="flex-1 text-xs px-3 py-2 rounded-lg outline-none" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.12)', color: 'rgba(212,197,169,0.8)' }} />
        <button onClick={() => setNewMsg('')} className="text-xs px-3 py-2 rounded-lg shrink-0" style={{ background: `${cohortColor}20`, color: cohortColor }}>Post</button>
      </div>
    </div>
  );
}

// ─── INSIGHTS WIDGET ──────────────────────────────────────────────────────────

function InsightsWidget({ metrics, goals, role }: { metrics: InsightMetrics; goals: InsightGoal[]; role: string }) {
  const [activeTab, setActiveTab] = useState<'metrics' | 'goals'>('metrics');
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [newGoalLabel, setNewGoalLabel] = useState('');
  const [newGoalTarget, setNewGoalTarget] = useState('');

  const roleColor = ROLE_COLORS[role] || '#c9a84c';

  const METRIC_ITEMS = [
    { label: 'Quests Completed', value: metrics.questsCompleted, icon: '⌘', color: '#D4A82A' },
    { label: 'Community Contributions', value: metrics.communityContributions, icon: '⬡', color: '#2d7a47' },
    { label: 'Garments Shared', value: metrics.garmentsShared, icon: '◈', color: '#2d8a87' },
    { label: 'Followers Gained', value: metrics.followersGained, icon: '◎', color: '#8a8ab8' },
    { label: 'Badges Earned', value: metrics.badgesEarned, icon: '✦', color: '#c9a84c' },
    { label: 'Alhamdulilah Count', value: metrics.alhamdulilahCount, icon: '☽', color: '#2d7a47' },
    { label: 'Modules Visited', value: metrics.modulesVisited, icon: '⬡', color: '#2d8a87' },
    { label: 'Reactions Received', value: metrics.reactionsReceived, icon: '✦', color: '#D4A82A' },
  ];

  const ROLE_IMPACT: Record<string, { label: string; value: string; icon: string }[]> = {
    'community-caretaker': [
      { label: 'People Served', value: '12', icon: '👥' },
      { label: 'Resources Mapped', value: '8', icon: '🗺️' },
      { label: 'Cases Resolved', value: '3', icon: '⚖️' },
    ],
    'inner-seeker': [
      { label: 'IRL Observations', value: '24', icon: '◎' },
      { label: 'Names Explored', value: '18', icon: '✦' },
      { label: 'Verses Witnessed', value: '31', icon: '☽' },
    ],
    'academic': [
      { label: 'Concepts Mapped', value: '15', icon: '◈' },
      { label: 'Cross-References', value: '42', icon: '⌘' },
      { label: 'Study Sessions', value: '9', icon: '📚' },
    ],
    'activist': [
      { label: 'Actions Taken', value: '7', icon: '⌘' },
      { label: 'People Mobilized', value: '34', icon: '👥' },
      { label: 'Resources Shared', value: '11', icon: '⬡' },
    ],
  };

  const roleImpact = ROLE_IMPACT[role] || ROLE_IMPACT['inner-seeker'];

  return (
    <div className="rounded-xl border p-5" style={{ background: `${roleColor}0d`, borderColor: `${roleColor}33` }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-sm" style={{ color: '#d4c5a9' }}>My Insights</h3>
          <p className="text-xs mt-0.5" style={{ color: roleColor }}>{ROLE_LABELS[role] || 'Caretaker'} · Impact</p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setActiveTab('metrics')} className="text-xs px-2 py-1 rounded-lg" style={{ background: activeTab === 'metrics' ? `${roleColor}25` : 'transparent', color: activeTab === 'metrics' ? roleColor : 'rgba(212,197,169,0.5)', border: `1px solid ${activeTab === 'metrics' ? `${roleColor}50` : 'transparent'}` }}>Metrics</button>
          <button onClick={() => setActiveTab('goals')} className="text-xs px-2 py-1 rounded-lg" style={{ background: activeTab === 'goals' ? `${roleColor}25` : 'transparent', color: activeTab === 'goals' ? roleColor : 'rgba(212,197,169,0.5)', border: `1px solid ${activeTab === 'goals' ? `${roleColor}50` : 'transparent'}` }}>Goals</button>
        </div>
      </div>

      {activeTab === 'metrics' && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {METRIC_ITEMS.map(m => (
              <div key={m.label} className="p-2.5 rounded-lg" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.08)' }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <span style={{ color: m.color }}>{m.icon}</span>
                  <span className="text-lg font-bold" style={{ color: m.color }}>{m.value}</span>
                </div>
                <p className="text-xs leading-tight" style={{ color: 'rgba(212,197,169,0.5)' }}>{m.label}</p>
              </div>
            ))}
          </div>
          <div className="p-3 rounded-lg" style={{ background: `${roleColor}0d`, border: `1px solid ${roleColor}25` }}>
            <p className="text-xs font-medium mb-2" style={{ color: roleColor }}>Role-Specific Impact</p>
            <div className="grid grid-cols-3 gap-2">
              {roleImpact.map(item => (
                <div key={item.label} className="text-center">
                  <div className="text-lg mb-0.5">{item.icon}</div>
                  <div className="text-sm font-bold" style={{ color: roleColor }}>{item.value}</div>
                  <div className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'goals' && (
        <>
          <div className="space-y-3 mb-4">
            {goals.map(goal => {
              const pct = Math.min(100, Math.round((goal.currentValue / goal.targetValue) * 100));
              return (
                <div key={goal.id} className="p-3 rounded-lg" style={{ background: 'rgba(212,197,169,0.03)', border: `1px solid ${goal.isCompleted ? `${roleColor}40` : 'rgba(212,197,169,0.08)'}` }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium" style={{ color: goal.isCompleted ? roleColor : 'rgba(212,197,169,0.8)' }}>{goal.goalLabel}</span>
                    <span className="text-xs" style={{ color: goal.isCompleted ? roleColor : 'rgba(212,197,169,0.5)' }}>{goal.currentValue}/{goal.targetValue}</span>
                  </div>
                  <div className="w-full rounded-full h-1.5" style={{ background: 'rgba(212,197,169,0.1)' }}>
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: goal.isCompleted ? roleColor : `${roleColor}80` }} />
                  </div>
                  {goal.isCompleted && <p className="text-xs mt-1" style={{ color: roleColor }}>✓ الحمد لله — Completed</p>}
                </div>
              );
            })}
          </div>
          {showAddGoal ? (
            <div className="p-3 rounded-lg mb-3" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.12)' }}>
              <input value={newGoalLabel} onChange={e => setNewGoalLabel(e.target.value)} placeholder="Goal description..." className="w-full text-xs px-3 py-2 rounded-lg outline-none mb-2" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.12)', color: 'rgba(212,197,169,0.8)' }} />
              <div className="flex gap-2">
                <input value={newGoalTarget} onChange={e => setNewGoalTarget(e.target.value)} placeholder="Target (e.g. 10)" type="number" className="flex-1 text-xs px-3 py-2 rounded-lg outline-none" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.12)', color: 'rgba(212,197,169,0.8)' }} />
                <button onClick={() => { setShowAddGoal(false); setNewGoalLabel(''); setNewGoalTarget(''); }} className="text-xs px-3 py-2 rounded-lg" style={{ background: `${roleColor}20`, color: roleColor }}>Set Goal</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAddGoal(true)} className="w-full text-xs py-2 rounded-lg transition-all" style={{ background: `${roleColor}10`, color: roleColor, border: `1px dashed ${roleColor}40` }}>
              + Set New Goal
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function FollowersPage() {
  const [activeTab, setActiveTab] = useState<'feed' | 'caretakers' | 'cohorts' | 'widgets' | 'insights'>('feed');
  const [caretakers, setCaretakers] = useState<FollowedCaretaker[]>(DEMO_CARETAKERS);
  const [activity, setActivity] = useState<ActivityItem[]>(DEMO_ACTIVITY);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [tasks] = useState<CommunityTask[]>(DEMO_TASKS);
  const [justiceCases] = useState<JusticeCase[]>(DEMO_JUSTICE_CASES);
  const [learningItems] = useState<LearningItem[]>(DEMO_LEARNING);
  const [resourceFavorites] = useState<ResourceFavorite[]>(DEMO_RESOURCES);
  const [cohortMessages] = useState<CohortMessage[]>(DEMO_COHORT_MESSAGES);
  const [goals] = useState<InsightGoal[]>(DEMO_GOALS);
  const [metrics] = useState<InsightMetrics>(DEMO_METRICS);
  const [selectedRole, setSelectedRole] = useState<string>('community-caretaker');
  const [feedFilter, setFeedFilter] = useState<string>('all');
  const [mutualOnly, setMutualOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const supabase = createClient();

  useEffect(() => {
    loadCohorts();
    loadActivity();
  }, []);

  const loadCohorts = async () => {
    try {
      const { data } = await supabase.from('role_cohorts').select('*').eq('is_active', true).order('member_count', { ascending: false });
      if (data && data.length > 0) {
        setCohorts(data.map((c: any) => ({
          id: c.id,
          name: c.name,
          role: c.role,
          description: c.description,
          icon: c.icon,
          color: c.color,
          memberCount: c.member_count,
        })));
      } else {
        setCohorts([
          { id: 'co1', name: 'Community Caretakers Network', role: 'community-caretaker', description: 'Caretakers using the MRG and justice resources to serve their communities', icon: '⬡', color: '#2d7a47', memberCount: 83 },
          { id: 'co2', name: 'Inner Seekers Circle', role: 'inner-seeker', description: 'A space for those walking the inward path — IRL Lens practitioners and Ayat witnesses', icon: '◎', color: '#8a8ab8', memberCount: 47 },
          { id: 'co3', name: 'Activist Collective', role: 'activist', description: 'Organizers grounding their work in Islamic ontology', icon: '⌘', color: '#2d8a87', memberCount: 61 },
          { id: 'co4', name: 'Academic Scholars Guild', role: 'academic', description: 'Researchers engaging the framework through classical lenses', icon: '✦', color: '#d4a82a', memberCount: 29 },
        ]);
      }
    } catch {
      setCohorts([
        { id: 'co1', name: 'Community Caretakers Network', role: 'community-caretaker', description: 'Caretakers using the MRG and justice resources to serve their communities', icon: '⬡', color: '#2d7a47', memberCount: 83 },
        { id: 'co2', name: 'Inner Seekers Circle', role: 'inner-seeker', description: 'A space for those walking the inward path', icon: '◎', color: '#8a8ab8', memberCount: 47 },
        { id: 'co3', name: 'Activist Collective', role: 'activist', description: 'Organizers grounding their work in Islamic ontology', icon: '⌘', color: '#2d8a87', memberCount: 61 },
        { id: 'co4', name: 'Academic Scholars Guild', role: 'academic', description: 'Researchers engaging the framework through classical lenses', icon: '✦', color: '#d4a82a', memberCount: 29 },
      ]);
    }
  };

  const loadActivity = async () => {
    try {
      const { data } = await supabase.from('follower_activity').select('*').eq('is_public', true).order('created_at', { ascending: false }).limit(20);
      if (data && data.length > 0) {
        setActivity(data.map((a: any) => ({
          id: a.id,
          actorName: a.actor_name || 'Anonymous',
          actorRole: a.actor_role || 'inner-seeker',
          activityType: a.activity_type,
          activityLabel: a.activity_label,
          activityDetail: a.activity_detail,
          createdAt: timeAgo(a.created_at),
        })));
      }
    } catch { /* use demo data */ }
  };

  const toggleFollow = (id: string) => {
    setCaretakers(prev => prev.map(c => c.id === id ? { ...c, isFollowing: !c.isFollowing } : c));
  };

  const filteredActivity = feedFilter === 'all' ? activity : activity.filter(a => a.activityType === feedFilter);
  const filteredCaretakers = mutualOnly ? caretakers.filter(c => c.isMutual) : caretakers;
  const followedCaretakers = caretakers.filter(c => c.isFollowing);
  const mutualCaretakers = caretakers.filter(c => c.isMutual);
  const suggestedCaretakers = caretakers.filter(c => !c.isFollowing);

  const TABS = [
    { id: 'feed', label: 'Activity Feed', icon: '◎' },
    { id: 'caretakers', label: 'Following', icon: '⬡' },
    { id: 'cohorts', label: 'Cohorts', icon: '⌘' },
    { id: 'widgets', label: 'Widgets', icon: '◈' },
    { id: 'insights', label: 'Insights', icon: '✦' },
  ] as const;

  const selectedCohort = cohorts.find(c => c.role === selectedRole) || cohorts[0];

  return (
    <div style={{ minHeight: '100vh', background: '#060D12', color: '#F5F0E8' }}>
      <Header />
      <Breadcrumb items={[{ label: 'Community', href: '/community' }, { label: 'Followers' }]} />
      <main style={{ maxWidth: '100%', width: '100%', margin: '0 auto', padding: 'clamp(96px, 15vw, 112px) clamp(12px, 4vw, 24px) clamp(64px, 10vw, 96px)' }}>
        <div style={{ maxWidth: 1024, margin: '0 auto' }}>

          {/* ── HERO ── */}
          <div className="mb-10">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs tracking-widest uppercase mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Community · المجتمع</p>
                <h1 style={{ fontSize: 'clamp(22px, 5vw, 32px)', fontWeight: 700, marginBottom: 8, color: '#d4c5a9', fontFamily: 'Georgia, serif' }}>
                  Followers & Cohorts
                </h1>
                <p className="text-sm max-w-lg" style={{ color: 'rgba(212,197,169,0.6)' }}>
                  Follow caretakers on their journey, join role-based cohorts, and race each other in good — fisabilillah.
                </p>
              </div>
              <div className="flex gap-4 text-center flex-wrap">
                <div className="px-4 py-3 rounded-xl" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.1)' }}>
                  <div className="text-xl font-bold" style={{ color: '#d4c5a9' }}>{followedCaretakers.length}</div>
                  <div className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>Following</div>
                </div>
                <div className="px-4 py-3 rounded-xl" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.1)' }}>
                  <div className="text-xl font-bold" style={{ color: '#d4c5a9' }}>{metrics.followersGained}</div>
                  <div className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>Followers</div>
                </div>
                <div className="px-4 py-3 rounded-xl" style={{ background: 'rgba(212,197,169,0.04)', border: '1px solid rgba(212,197,169,0.1)' }}>
                  <div className="text-xl font-bold" style={{ color: '#d4c5a9' }}>{mutualCaretakers.length}</div>
                  <div className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>Mutual</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── TABS ── */}
          <div className="flex gap-1 mb-8 overflow-x-auto pb-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all"
                style={{
                  background: activeTab === tab.id ? 'rgba(212,197,169,0.1)' : 'transparent',
                  color: activeTab === tab.id ? '#d4c5a9' : 'rgba(212,197,169,0.45)',
                  border: `1px solid ${activeTab === tab.id ? 'rgba(212,197,169,0.2)' : 'transparent'}`,
                }}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── ACTIVITY FEED ── */}
          {activeTab === 'feed' && (
            <div>
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="text-xs" style={{ color: 'rgba(212,197,169,0.5)' }}>Filter:</span>
                {['all', 'quest_completed', 'badge_earned', 'garment_shared', 'milestone'].map(f => (
                  <button key={f} onClick={() => setFeedFilter(f)} className="text-xs px-3 py-1.5 rounded-lg transition-all capitalize" style={{ background: feedFilter === f ? 'rgba(212,197,169,0.1)' : 'transparent', color: feedFilter === f ? '#d4c5a9' : 'rgba(212,197,169,0.45)', border: `1px solid ${feedFilter === f ? 'rgba(212,197,169,0.2)' : 'rgba(212,197,169,0.08)'}` }}>
                    {f === 'all' ? 'All' : f.replace('_', ' ')}
                  </button>
                ))}
              </div>
              <div className="space-y-3">
                {filteredActivity.map(item => (
                  <div key={item.id} className="flex gap-4 p-4 rounded-xl transition-all" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-base" style={{ background: `${ACTIVITY_COLORS[item.activityType] || '#c9a84c'}20`, color: ACTIVITY_COLORS[item.activityType] || '#c9a84c' }}>
                      {ACTIVITY_ICONS[item.activityType] || '◎'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium" style={{ color: 'rgba(212,197,169,0.9)' }}>{item.actorName}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: `${ROLE_COLORS[item.actorRole] || '#c9a84c'}20`, color: ROLE_COLORS[item.actorRole] || '#c9a84c' }}>
                          {ROLE_ICONS[item.actorRole]} {ROLE_LABELS[item.actorRole] || item.actorRole}
                        </span>
                      </div>
                      <p className="text-sm font-medium mb-0.5" style={{ color: 'rgba(212,197,169,0.75)' }}>{item.activityLabel}</p>
                      {item.activityDetail && <p className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>{item.activityDetail}</p>}
                    </div>
                    <span className="text-xs shrink-0 mt-1" style={{ color: 'rgba(212,197,169,0.35)' }}>{item.createdAt}</span>
                  </div>
                ))}
                {filteredActivity.length === 0 && (
                  <div className="text-center py-12" style={{ color: 'rgba(212,197,169,0.4)' }}>
                    <p className="text-4xl mb-3">◎</p>
                    <p className="text-sm">No activity yet. Follow caretakers to see their milestones here.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── FOLLOWING / CARETAKERS ── */}
          {activeTab === 'caretakers' && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <div onClick={() => setMutualOnly(!mutualOnly)} className="w-9 h-5 rounded-full relative transition-all" style={{ background: mutualOnly ? '#2d7a47' : 'rgba(212,197,169,0.15)' }}>
                    <div className="w-4 h-4 rounded-full absolute top-0.5 transition-all" style={{ background: '#d4c5a9', left: mutualOnly ? '18px' : '2px' }} />
                  </div>
                  <span className="text-xs" style={{ color: 'rgba(212,197,169,0.6)' }}>Mutual connections only</span>
                </label>
              </div>

              {/* Following */}
              <div className="mb-8">
                <h2 className="text-sm font-semibold mb-4" style={{ color: 'rgba(212,197,169,0.6)' }}>Following ({followedCaretakers.length})</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredCaretakers.filter(c => c.isFollowing).map(c => (
                    <div key={c.id} className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-base shrink-0" style={{ background: `${ROLE_COLORS[c.role]}20`, color: ROLE_COLORS[c.role] }}>
                        {ROLE_ICONS[c.role]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium" style={{ color: '#d4c5a9' }}>{c.name}</span>
                          {c.isMutual && <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(45,122,71,0.15)', color: '#2d7a47' }}>Mutual</span>}
                        </div>
                        <p className="text-xs mb-1" style={{ color: ROLE_COLORS[c.role] }}>{ROLE_LABELS[c.role]}{c.city ? ` · ${c.city}` : ''}</p>
                        <div className="flex items-center gap-3">
                          <span className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>Stage: {c.stage}</span>
                          <span className="text-xs" style={{ color: 'rgba(212,197,169,0.45)' }}>⌘ {c.questsCompleted} quests</span>
                        </div>
                        <p className="text-xs mt-1" style={{ color: 'rgba(212,197,169,0.3)' }}>Active {c.lastActive}</p>
                      </div>
                      <button onClick={() => toggleFollow(c.id)} className="text-xs px-2 py-1 rounded-lg shrink-0" style={{ background: 'rgba(212,197,169,0.06)', color: 'rgba(212,197,169,0.5)', border: '1px solid rgba(212,197,169,0.12)' }}>
                        Unfollow
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mutual Discovery */}
              {mutualCaretakers.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-sm font-semibold mb-2" style={{ color: 'rgba(212,197,169,0.6)' }}>Mutual Connections</h2>
                  <p className="text-xs mb-4" style={{ color: 'rgba(212,197,169,0.4)' }}>Caretakers who follow you back — your closest community ties.</p>
                  <div className="flex flex-wrap gap-2">
                    {mutualCaretakers.map(c => (
                      <div key={c.id} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.1)' }}>
                        <span style={{ color: ROLE_COLORS[c.role] }}>{ROLE_ICONS[c.role]}</span>
                        <span className="text-xs font-medium" style={{ color: '#d4c5a9' }}>{c.name}</span>
                        <span className="text-xs" style={{ color: '#2d7a47' }}>↔</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested */}
              {suggestedCaretakers.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold mb-2" style={{ color: 'rgba(212,197,169,0.6)' }}>Discover Caretakers</h2>
                  <p className="text-xs mb-4" style={{ color: 'rgba(212,197,169,0.4)' }}>Caretakers you may resonate with based on shared pathways.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {suggestedCaretakers.map(c => (
                      <div key={c.id} className="p-4 rounded-xl flex items-start gap-3" style={{ background: 'rgba(212,197,169,0.02)', border: '1px solid rgba(212,197,169,0.06)' }}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-base shrink-0" style={{ background: `${ROLE_COLORS[c.role]}15`, color: ROLE_COLORS[c.role] }}>
                          {ROLE_ICONS[c.role]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium block mb-0.5" style={{ color: '#d4c5a9' }}>{c.name}</span>
                          <p className="text-xs mb-1" style={{ color: ROLE_COLORS[c.role] }}>{ROLE_LABELS[c.role]}{c.city ? ` · ${c.city}` : ''}</p>
                          <span className="text-xs" style={{ color: 'rgba(212,197,169,0.4)' }}>Stage: {c.stage} · {c.questsCompleted} quests</span>
                        </div>
                        <button onClick={() => toggleFollow(c.id)} className="text-xs px-3 py-1.5 rounded-lg shrink-0 font-medium" style={{ background: 'rgba(45,122,71,0.15)', color: '#2d7a47', border: '1px solid rgba(45,122,71,0.3)' }}>
                          Follow
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── COHORTS ── */}
          {activeTab === 'cohorts' && (
            <div>
              <p className="text-sm mb-6" style={{ color: 'rgba(212,197,169,0.55)' }}>
                Role-based cohorts bring together caretakers with shared pathways. Join your cohort to access the message board and shared resources.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {cohorts.map(cohort => (
                  <button
                    key={cohort.id}
                    onClick={() => setSelectedRole(cohort.role)}
                    className="p-5 rounded-xl text-left transition-all"
                    style={{
                      background: selectedRole === cohort.role ? `${cohort.color}12` : 'rgba(212,197,169,0.03)',
                      border: `1px solid ${selectedRole === cohort.role ? `${cohort.color}40` : 'rgba(212,197,169,0.08)'}`,
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg" style={{ background: `${cohort.color}20`, color: cohort.color }}>
                        {cohort.icon}
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold" style={{ color: '#d4c5a9' }}>{cohort.name}</h3>
                        <p className="text-xs" style={{ color: cohort.color }}>{cohort.memberCount} members</p>
                      </div>
                    </div>
                    <p className="text-xs leading-relaxed" style={{ color: 'rgba(212,197,169,0.5)' }}>{cohort.description}</p>
                    {selectedRole === cohort.role && (
                      <div className="mt-3 text-xs px-2 py-1 rounded-lg inline-block" style={{ background: `${cohort.color}20`, color: cohort.color }}>
                        ✓ Active
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {selectedCohort && (
                <CohortMessageBoardWidget
                  messages={cohortMessages}
                  cohortName={selectedCohort.name}
                  cohortColor={selectedCohort.color}
                />
              )}
            </div>
          )}

          {/* ── ROLE WIDGETS ── */}
          {activeTab === 'widgets' && (
            <div>
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="text-xs" style={{ color: 'rgba(212,197,169,0.5)' }}>Role:</span>
                {Object.entries(ROLE_LABELS).map(([role, label]) => (
                  <button key={role} onClick={() => setSelectedRole(role)} className="text-xs px-3 py-1.5 rounded-lg transition-all" style={{ background: selectedRole === role ? `${ROLE_COLORS[role]}20` : 'transparent', color: selectedRole === role ? ROLE_COLORS[role] : 'rgba(212,197,169,0.45)', border: `1px solid ${selectedRole === role ? `${ROLE_COLORS[role]}40` : 'rgba(212,197,169,0.08)'}` }}>
                    {ROLE_ICONS[role]} {label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Prayer Tracker — all roles */}
                <PrayerTrackerWidget />

                {/* Community Task Board — community-caretaker, activist */}
                {(selectedRole === 'community-caretaker' || selectedRole === 'activist') && (
                  <CommunityTaskBoardWidget tasks={tasks} />
                )}

                {/* Justice Case Log — community-caretaker, activist */}
                {(selectedRole === 'community-caretaker' || selectedRole === 'activist') && (
                  <JusticeCaseLogWidget cases={justiceCases} />
                )}

                {/* Learning Schedule — academic, inner-seeker */}
                {(selectedRole === 'academic' || selectedRole === 'inner-seeker') && (
                  <LearningScheduleWidget items={learningItems} />
                )}

                {/* Resource Favorites — all roles */}
                <ResourceFavoritesWidget resources={resourceFavorites} />

                {/* Cohort Message Board — all roles */}
                {selectedCohort && (
                  <CohortMessageBoardWidget
                    messages={cohortMessages}
                    cohortName={selectedCohort.name}
                    cohortColor={selectedCohort.color}
                  />
                )}

                {/* Inner Seeker specific: IRL Observations quick-link */}
                {selectedRole === 'inner-seeker' && (
                  <div className="rounded-xl border p-5" style={{ background: 'rgba(138,138,184,0.06)', borderColor: 'rgba(138,138,184,0.2)' }}>
                    <h3 className="font-semibold text-sm mb-1" style={{ color: '#d4c5a9' }}>IRL Lens Quick Access</h3>
                    <p className="text-xs mb-4" style={{ color: 'rgba(212,197,169,0.5)' }}>Your personal Inner Reality Lens — simplified for daily practice</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'IRL Lens', href: '/inner-reality-lens', color: '#2d8a87', icon: '◎' },
                        { label: 'IRL Walkthrough', href: '/irl-lens', color: '#8a8ab8', icon: '◈' },
                        { label: 'Ayat in Creation', href: '/ayat-in-creation', color: '#2d7a47', icon: '☽' },
                        { label: 'Names Reference', href: '/names-reference', color: '#8a8ab8', icon: '✦' },
                      ].map(item => (
                        <Link key={item.href} href={item.href} className="flex items-center gap-2 p-3 rounded-lg transition-all" style={{ background: `${item.color}10`, border: `1px solid ${item.color}25`, color: item.color }}>
                          <span>{item.icon}</span>
                          <span className="text-xs font-medium">{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Academic specific: Study tools */}
                {selectedRole === 'academic' && (
                  <div className="rounded-xl border p-5" style={{ background: 'rgba(212,168,42,0.06)', borderColor: 'rgba(212,168,42,0.2)' }}>
                    <h3 className="font-semibold text-sm mb-1" style={{ color: '#d4c5a9' }}>Scholarship Tools</h3>
                    <p className="text-xs mb-4" style={{ color: 'rgba(212,197,169,0.5)' }}>Deep-dive resources for academic engagement with the framework</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: 'Names Reference', href: '/names-reference', color: '#8a8ab8', icon: '✦' },
                        { label: 'Keys Framework', href: '/keys-to-kulli-haal', color: '#D4A82A', icon: '⌘' },
                        { label: 'SL Glossary', href: '/sl-glossary', color: '#c9a84c', icon: '◈' },
                        { label: 'Open Letter', href: '/open-letter', color: '#2d7a47', icon: '◎' },
                      ].map(item => (
                        <Link key={item.href} href={item.href} className="flex items-center gap-2 p-3 rounded-lg transition-all" style={{ background: `${item.color}10`, border: `1px solid ${item.color}25`, color: item.color }}>
                          <span>{item.icon}</span>
                          <span className="text-xs font-medium">{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── INSIGHTS ── */}
          {activeTab === 'insights' && (
            <div>
              <div className="flex items-center gap-2 mb-6 flex-wrap">
                <span className="text-xs" style={{ color: 'rgba(212,197,169,0.5)' }}>View as:</span>
                {Object.entries(ROLE_LABELS).map(([role, label]) => (
                  <button key={role} onClick={() => setSelectedRole(role)} className="text-xs px-3 py-1.5 rounded-lg transition-all" style={{ background: selectedRole === role ? `${ROLE_COLORS[role]}20` : 'transparent', color: selectedRole === role ? ROLE_COLORS[role] : 'rgba(212,197,169,0.45)', border: `1px solid ${selectedRole === role ? `${ROLE_COLORS[role]}40` : 'rgba(212,197,169,0.08)'}` }}>
                    {ROLE_ICONS[role]} {label}
                  </button>
                ))}
              </div>
              <InsightsWidget metrics={metrics} goals={goals} role={selectedRole} />
              <div className="mt-6 p-5 rounded-xl" style={{ background: 'rgba(212,197,169,0.03)', border: '1px solid rgba(212,197,169,0.08)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#d4c5a9' }}>Racing Fisabilillah</h3>
                <p className="text-xs leading-relaxed mb-4" style={{ color: 'rgba(212,197,169,0.55)' }}>
                  "And for this let the competitors compete." (83:26) — Your journey is not measured against others, but your own growth in recognition and service. The الحمد لله count reflects your engagement with the framework, not a score to win.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {followedCaretakers.slice(0, 4).map(c => (
                    <div key={c.id} className="p-3 rounded-lg text-center" style={{ background: `${ROLE_COLORS[c.role]}0d`, border: `1px solid ${ROLE_COLORS[c.role]}25` }}>
                      <div className="text-xl mb-1" style={{ color: ROLE_COLORS[c.role] }}>{ROLE_ICONS[c.role]}</div>
                      <div className="text-xs font-medium mb-0.5" style={{ color: '#d4c5a9' }}>{c.name.split(' ')[0]}</div>
                      <div className="text-xs" style={{ color: ROLE_COLORS[c.role] }}>Stage: {c.stage}</div>
                      <div className="text-xs mt-1" style={{ color: 'rgba(212,197,169,0.4)' }}>⌘ {c.questsCompleted}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
      <Footer />
    </div>
  );
}
